import { ArrowRight, BarChart3 } from "lucide-react";
import { Link } from "react-router";
import { ChangeIndicator } from "~/components/company/financials/change-indicator";
import { Sparkline } from "~/components/company/financials/sparkline";
import { formatCurrency } from "~/lib/format";
import type { CompanyProfile, FinancialSummary } from "~/types/company";
import { CardFooterLink, OverviewCard } from "./card";

type FinancialKey = "revenue" | "netProfit" | "totalAssets" | "equity";

function computeYoY(financials: FinancialSummary[], key: FinancialKey): number | null {
	if (financials.length < 2) return null;
	const current = financials[0][key];
	const prior = financials[1][key];
	if (current == null || prior == null || prior === 0) return null;
	return (current - prior) / Math.abs(prior);
}

function KpiItem({
	label,
	value,
	change,
	sparkData,
}: {
	label: string;
	value: string;
	change?: number | null;
	sparkData?: (number | null)[];
}) {
	return (
		<div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4">
			<p className="text-xs font-medium text-muted-foreground">{label}</p>
			<p className="text-xl font-mono font-semibold tabular-nums">{value}</p>
			{(change != null || sparkData) && (
				<div className="flex items-center justify-between mt-1">
					{change != null ? <ChangeIndicator value={change} /> : <span />}
					{sparkData && sparkData.length > 1 && <Sparkline data={sparkData} />}
				</div>
			)}
		</div>
	);
}

/** ② Finanční přehled — latest-year KPIs; empty state routes to /dokumenty. */
export function FinancialCard({ company }: { company: CompanyProfile }) {
	const latest = company.financials[0] ?? null;

	if (!latest) {
		return (
			<OverviewCard icon={BarChart3} title="Finanční přehled" hue="teal">
				<div className="flex flex-col items-center gap-3 py-6 text-center">
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
						<BarChart3 className="h-6 w-6 text-muted-foreground" />
					</div>
					<p className="text-sm font-medium">Žádné finanční údaje</p>
					<Link
						to="dokumenty"
						prefetch="intent"
						className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
					>
						Účetní závěrky čekají na zpracování — zobrazit dokumenty
						<ArrowRight className="h-3.5 w-3.5" />
					</Link>
				</div>
			</OverviewCard>
		);
	}

	return (
		<OverviewCard hue="teal" icon={BarChart3} title={`Finanční přehled (${latest.year})`}>
			<div className="grid gap-4 grid-cols-2">
				<KpiItem
					label="Tržby"
					value={formatCurrency(latest.revenue)}
					change={computeYoY(company.financials, "revenue")}
					sparkData={company.financials.map((f) => f.revenue).reverse()}
				/>
				<KpiItem
					label="Čistý zisk"
					value={formatCurrency(latest.netProfit)}
					change={computeYoY(company.financials, "netProfit")}
					sparkData={company.financials.map((f) => f.netProfit).reverse()}
				/>
				<KpiItem
					label="Aktiva"
					value={formatCurrency(latest.totalAssets)}
					change={computeYoY(company.financials, "totalAssets")}
					sparkData={company.financials.map((f) => f.totalAssets).reverse()}
				/>
				<KpiItem
					label="Vlastní kapitál"
					value={formatCurrency(latest.equity)}
					change={computeYoY(company.financials, "equity")}
					sparkData={company.financials.map((f) => f.equity).reverse()}
				/>
			</div>
			<CardFooterLink to="finance">Kompletní finance</CardFooterLink>
		</OverviewCard>
	);
}
